import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { DISPLAY_UNIQUE_ID_WITH_USER_NAME } from '@app/constants/permissions';
import {
  QUERY_KEYS,
  OPERATORS_BULK_UPLOAD_STATUS_QUERY_KEY,
} from '@app/constants/service';
import usePermissionVerify, {
  verifyPermission,
} from '@app/hooks/permission-verify';
import queryClient from '@app/query-client';
import {
  fetchOperators,
  fetchBulkUploadStatus,
} from '@app/services/operator/handler';
import { User } from '@app/types/api/user';

const buildOperatorsQuery = (
  customFilters: {
    searchText?: string;
    ids?: string[];
    groups?: string[];
    status?: string;
    order?: string;
    sort?: string;
    limit?: number;
    includeGroupDetails?: boolean;
    includeOktaUserId?: boolean;
  },
  options?: {
    enabled?: boolean;
  },
) => {
  const filters = {
    limit: 32,
    role: 'operator',
    status: 'active',
    sort: 'profile.firstName',
    order: 'ASC',
    ...customFilters,
  };

  return {
    queryKey: [QUERY_KEYS.OPERATORS, filters],
    queryFn: ({ pageParam = 1 }) => fetchOperators(filters, pageParam),
    getNextPageParam: filters.searchText?.trim()
      ? undefined
      : (lastPage: { operators: User[] }, pages: { operators: User[] }[]) => {
          if ((lastPage?.operators?.length || 0) < filters.limit)
            return undefined;
          return (pages?.length || 0) + 1;
        },
    ...options,
  };
};

// for react component
export const useOperatorsQuery = (
  customFilters: {
    searchText?: string;
    ids?: string[];
    groups?: string[];
    status?: string;
    order?: string;
    sort?: string;
    limit?: number;
    includeGroupDetails?: boolean;
    from?: string;
  },
  options?: {
    enabled?: boolean;
  },
) => {
  const { DISPLAY_UNIQUE_ID_WITH_USER_NAME: showUniqueId } =
    usePermissionVerify([DISPLAY_UNIQUE_ID_WITH_USER_NAME]);

  const filtersWithPermission = {
    ...customFilters,
    includeOktaUserId: !!showUniqueId,
  };

  return useInfiniteQuery(buildOperatorsQuery(filtersWithPermission, options));
};

const BULK_UPLOAD_STATUS_POLL_INTERVAL_MS = 5000;

export const useBulkUploadStatus = (enabled = true) =>
  useQuery({
    queryKey: [...OPERATORS_BULK_UPLOAD_STATUS_QUERY_KEY],
    queryFn: fetchBulkUploadStatus,
    enabled,
    refetchInterval: (data) =>
      data?.inProgress ? BULK_UPLOAD_STATUS_POLL_INTERVAL_MS : false,
  });

// for react-router loader
export const prefetchOperatorsQuery = (
  customFilters: {
    searchText?: string;
    ids?: string[];
    groups?: string[];
    status?: string;
    order?: string;
    sort?: string;
    limit?: number;
    includeGroupDetails?: boolean;
  },
  options?: {
    enabled?: boolean;
    staleTime?: number;
  },
) => {
  // we use to verifyPermission retrieve data because hook cannot be used outside react component
  const { DISPLAY_UNIQUE_ID_WITH_USER_NAME: showUniqueId } = verifyPermission([
    DISPLAY_UNIQUE_ID_WITH_USER_NAME,
  ]);
  const filtersWithPermission = {
    ...customFilters,
    includeOktaUserId: !!showUniqueId,
  };
  queryClient.prefetchInfiniteQuery(
    buildOperatorsQuery(filtersWithPermission, options),
  );
};
